import { useState, useCallback, useEffect, useRef } from 'react';
import { webrtcService } from '../services/webrtc';
import { signalingService } from '../services/signaling';
import { localDiscoveryService } from '../services/localDiscovery';
import { fileHistoryService } from '../services/fileHistory';
import { validateFileSize } from '../utils/fileUtils';
import type { FileTransfer, PeerData, UserTier, DiscoveryMethod } from '../types';

/**
 * Custom hook for managing file transfers with automatic signaling
 */
export function useFileTransfer(userTier: UserTier, userId?: string, spaceId?: string, userEmail?: string) {
  const [transfers, setTransfers] = useState<FileTransfer[]>([]);
  const [remotePeer, setRemotePeer] = useState<PeerData | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [peerCount, setPeerCount] = useState(0);
  const [localPeerName, setLocalPeerName] = useState(() => {
    // If user is signed in, use email prefix as default name
    if (userEmail) {
      const emailPrefix = userEmail.split('@')[0];
      signalingService.updatePeerNameLocally(emailPrefix);
      return emailPrefix;
    }
    return signalingService.getPeerName();
  });
  const [receivedFile, setReceivedFile] = useState<{ file: File; blob: Blob } | null>(null);
  
  // Local discovery state - enabled by default, stored in localStorage
  const [isLocalDiscoveryEnabled, setIsLocalDiscoveryEnabled] = useState(() => {
    const stored = localStorage.getItem('localDiscoveryEnabled');
    return stored !== null ? stored === 'true' : true; // Default to true
  });
  const [localPeers, setLocalPeers] = useState<PeerData[]>([]);
  const [activeConnection, setActiveConnection] = useState<{ peerId: string; method: DiscoveryMethod } | null>(null);
  const onMessageReceivedCallbackRef = useRef<((htmlContent: string, from: string) => void) | null>(null);

  // Update peer name when user email changes
  useEffect(() => {
    if (userEmail) {
      const emailPrefix = userEmail.split('@')[0];
      setLocalPeerName(emailPrefix);
      signalingService.updatePeerNameLocally(emailPrefix);
    }
  }, [userEmail]);

  // Auto-enable local discovery if it's enabled in settings
  useEffect(() => {
    if (isLocalDiscoveryEnabled && !localDiscoveryService.isEnabled()) {
      enableLocalDiscovery();
    }
  }, []); // Run once on mount

  // Set up WebRTC and signaling callbacks
  useEffect(() => {
    webrtcService.setCallbacks({
      onTransferProgress: (transfer) => {
        setTransfers((prev) => {
          const index = prev.findIndex((t) => t.id === transfer.id);
          if (index >= 0) {
            const updated = [...prev];
            updated[index] = transfer;
            return updated;
          }
          return [...prev, transfer];
        });
      },
      onTransferComplete: async (transfer) => {
        setTransfers((prev) => {
          const updated = prev.map((t) =>
            t.id === transfer.id ? transfer : t
          );
          return updated;
        });

        // Determine if this is a sent or received file based on transfer ID
        // Received files have IDs that end with '_receive'
        const isReceived = transfer.id.endsWith('_receive');

        // Always add to history (even for anonymous users)
        await fileHistoryService.addToHistory({
          user_id: userId || null,
          filename: transfer.file.name,
          file_size: transfer.file.size,
          peer_id: transfer.peer.id,
          peer_name: transfer.peer.name,
          transfer_type: isReceived ? 'received' : 'sent',
        });
      },
      onTransferError: (errorMsg) => {
        setError(errorMsg);
      },
      onPeerConnected: (peer) => {
        console.log('🎉 Peer connected callback triggered:', peer);
        setRemotePeer(peer);
        setIsConnected(true);
        setError(null);
      },
      onPeerDisconnected: () => {
        setRemotePeer(null);
        setIsConnected(false);
      },
      onOfferCreated: async (offer) => {
        // Send offer through appropriate channel based on active connection
        try {
          if (activeConnection?.method === 'local') {
            console.log('📤 Sending offer via local discovery');
            await localDiscoveryService.sendOffer(activeConnection.peerId, offer);
          } else {
            console.log('📤 Sending offer via space signaling');
            await signalingService.sendOffer(offer);
          }
        } catch (err) {
          setError('Failed to send offer');
        }
      },
      onAnswerCreated: async (answer) => {
        // Send answer through appropriate channel based on active connection
        try {
          if (activeConnection?.method === 'local') {
            console.log('📤 Sending answer via local discovery');
            await localDiscoveryService.sendAnswer(activeConnection.peerId, answer);
          } else {
            console.log('📤 Sending answer via space signaling');
            await signalingService.sendAnswer(answer);
          }
        } catch (err) {
          setError('Failed to send answer');
        }
      },
      onFileReceived: (file, blob) => {
        console.log('📥 File received in hook:', file.name);
        setReceivedFile({ file, blob });
      },
      onMessageReceived: (htmlContent, from) => {
        console.log('💬 Message received in hook from:', from);
        if (onMessageReceivedCallbackRef.current) {
          onMessageReceivedCallbackRef.current(htmlContent, from);
        }
      },
    });

    signalingService.setCallbacks({
      onPeerJoined: async (remotePeerData: PeerData) => {
        console.log('🤝 Peer joined, establishing connection...');
        setPeerCount(2);
        
        // Set the remote peer data in WebRTC service before creating connection
        webrtcService.setRemotePeerData(remotePeerData);
        
        // If we're the initiator, create offer
        if (signalingService.getIsInitiator()) {
          try {
            await webrtcService.createOffer();
          } catch (err) {
            setError('Failed to create connection');
          }
        }
      },
      onPeerLeft: () => {
        console.log('👋 Peer left');
        setPeerCount(signalingService.getPeerCount());
        webrtcService.cleanup();
        setIsConnected(false);
        setRemotePeer(null);
      },
      onOfferReceived: async (offer) => {
        // Only responder (non-initiator) should process offers
        if (!signalingService.getIsInitiator()) {
          console.log('📥 Responder processing offer...');
          try {
            await webrtcService.answerOffer(offer);
          } catch (err) {
            setError('Failed to answer connection');
          }
        } else {
          console.log('⚠️ Initiator ignoring offer (already sent one)');
        }
      },
      onAnswerReceived: async (answer) => {
        // Only initiator should process answers
        if (signalingService.getIsInitiator()) {
          console.log('📥 Initiator processing answer...');
          try {
            await webrtcService.processAnswer(answer);
          } catch (err) {
            setError('Failed to complete connection');
          }
        } else {
          console.log('⚠️ Responder ignoring answer (not for them)');
        }
      },
    });

    // Set up local discovery callbacks
    localDiscoveryService.setCallbacks({
      onPeersChanged: (peers) => {
        console.log('🔍 Local peers changed:', peers.length);
        setLocalPeers(peers);
      },
      onOfferReceived: async (peerId, offer) => {
        console.log('📥 Received local offer from:', peerId);
        const peer = localDiscoveryService.getPeerById(peerId);
        if (peer) {
          webrtcService.setRemotePeerData(peer);
          setActiveConnection({ peerId, method: 'local' as DiscoveryMethod });
          try {
            await webrtcService.answerOffer(offer);
            // Send answer back via local discovery
            const answer = await new Promise<RTCSessionDescriptionInit>((resolve) => {
              webrtcService.setCallbacks({
                ...webrtcService,
                onAnswerCreated: (ans) => resolve(ans),
              });
            });
            await localDiscoveryService.sendAnswer(peerId, answer);
          } catch (err) {
            setError('Failed to answer local connection');
          }
        }
      },
      onAnswerReceived: async (peerId, answer) => {
        console.log('📥 Received local answer from:', peerId);
        try {
          await webrtcService.processAnswer(answer);
        } catch (err) {
          setError('Failed to complete local connection');
        }
      },
    });
  }, [userId, spaceId, activeConnection]);

  /**
   * Join space and start automatic signaling
   */
  const joinSpace = useCallback(async (spaceId: string) => {
    setError(null);
    try {
      await signalingService.joinSpace(spaceId);
      setPeerCount(signalingService.getPeerCount());
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to join space';
      setError(errorMsg);
    }
  }, []);

  /**
   * Start sending a file
   */
  const sendFile = useCallback(
    async (file: File) => {
      setError(null);

      // Validate file size - no need to set error here since it's handled in the modal
      const validationError = validateFileSize(file.size, userTier);
      if (validationError) {
        return false;
      }

      try {
        await webrtcService.sendFile(file);
        return true;
      } catch (err) {
        const errorMsg = err instanceof Error ? err.message : 'Failed to send file';
        setError(errorMsg);
        return false;
      }
    },
    [userTier]
  );

  /**
   * Update local peer name
   */
  const updatePeerName = useCallback(async (newName: string) => {
    setLocalPeerName(newName);
    await signalingService.updatePeerName(newName);
  }, []);

  /**
   * Download received file
   */
  const downloadReceivedFile = useCallback(() => {
    if (!receivedFile) return;

    const url = URL.createObjectURL(receivedFile.blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = receivedFile.file.name;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    // Clear the received file
    setReceivedFile(null);
  }, [receivedFile]);

  /**
   * Clear received file without downloading
   */
  const clearReceivedFile = useCallback(() => {
    setReceivedFile(null);
  }, []);

  /**
   * Enable local discovery
   */
  const enableLocalDiscovery = useCallback(async () => {
    setError(null);
    const success = await localDiscoveryService.enable(localPeerName);
    if (success) {
      setIsLocalDiscoveryEnabled(true);
      localStorage.setItem('localDiscoveryEnabled', 'true');
      console.log('✅ Local discovery enabled');
    } else {
      setError('Failed to enable local discovery');
    }
  }, [localPeerName]);

  /**
   * Disable local discovery
   */
  const disableLocalDiscovery = useCallback(async () => {
    await localDiscoveryService.disable();
    setIsLocalDiscoveryEnabled(false);
    localStorage.setItem('localDiscoveryEnabled', 'false');
    setLocalPeers([]);
    console.log('🚫 Local discovery disabled');
  }, []);

  /**
   * Send text message to connected peer
   */
  const sendMessage = useCallback(
    async (htmlContent: string) => {
      setError(null);

      try {
        await webrtcService.sendMessage(htmlContent);
        return true;
      } catch (err) {
        const errorMsg = err instanceof Error ? err.message : 'Failed to send message';
        setError(errorMsg);
        return false;
      }
    },
    []
  );

  /**
   * Set callback for when a message is received
   */
  const setOnMessageReceived = useCallback((callback: (htmlContent: string, from: string) => void) => {
    onMessageReceivedCallbackRef.current = callback;
  }, []);

  /**
   * Connect to a local peer
   */
  const connectToLocalPeer = useCallback(async (peer: PeerData) => {
    setError(null);
    console.log('🔗 Connecting to local peer:', peer.name);
    
    // Set active connection before creating offer so the callback knows to use local discovery
    setActiveConnection({ peerId: peer.id, method: 'local' as DiscoveryMethod });
    webrtcService.setRemotePeerData(peer);
    
    try {
      // Create offer - it will be automatically sent via local discovery by the onOfferCreated callback
      await webrtcService.createOffer();
      console.log('✅ Offer creation initiated for local peer');
    } catch (err) {
      console.error('❌ Failed to connect to local peer:', err);
      setError('Failed to connect to local peer');
    }
  }, []);

  /**
   * Cleanup connections
   */
  const cleanup = useCallback(async () => {
    webrtcService.cleanup();
    await signalingService.leaveSpace();
    if (isLocalDiscoveryEnabled) {
      await localDiscoveryService.disable();
    }
    setIsConnected(false);
    setRemotePeer(null);
    setPeerCount(0);
    setIsLocalDiscoveryEnabled(false);
    setLocalPeers([]);
    setActiveConnection(null);
  }, [isLocalDiscoveryEnabled]);

  return {
    transfers,
    remotePeer,
    isConnected,
    error,
    peerCount,
    localPeerName,
    receivedFile,
    joinSpace,
    sendFile,
    cleanup,
    updatePeerName,
    downloadReceivedFile,
    clearReceivedFile,
    // Local discovery
    isLocalDiscoveryEnabled,
    localPeers,
    enableLocalDiscovery,
    disableLocalDiscovery,
    connectToLocalPeer,
    // Text messages
    sendMessage,
    setOnMessageReceived,
  };
}
